//src/services/api.js

import axios from 'axios';

// Auto-detect environment
const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
export const API_BASE_URL = isLocalhost ? 'http://localhost:8000/api' : 'https://api.demo.schoolsync.ac/api';
export const BASE_URL = isLocalhost ? 'http://localhost:8000' : 'https://api.demo.schoolsync.ac'

const frontendEnv = (() => {
  if (isLocalhost) return 'local';

  const host = window.location.hostname.toLowerCase();
  if (host.includes('staging') || host.includes('dev')) {
    return 'staging';
  }

  return 'production';
})();

const ENVIRONMENT_SERVER_MESSAGES = {
  local: 'Server error. Check backend logs for details.',
  staging: 'Server error in this environment. Please contact support if it continues.',
  production: 'Something went wrong. Please try again later.',
};

const SENSITIVE_BACKEND_ERROR_PATTERNS = [
  /sqlstate/i,
  /pdoexception/i,
  /unknown database/i,
  /stack trace/i,
  /\sin\s.+\.php\s+on line\s+\d+/i,
  /syntax error or access violation/i,
];

const isSensitiveBackendMessage = (message) =>
  typeof message === 'string' &&
  SENSITIVE_BACKEND_ERROR_PATTERNS.some((pattern) => pattern.test(message));

const sanitizeErrorResponse = (error) => {
  if (!error?.response?.data || typeof error.response.data !== 'object') {
    return error;
  }

  const status = Number(error.response.status || 0);
  const data = error.response.data;
  const message = data.message;
  const shouldMaskMessage = status >= 500 || isSensitiveBackendMessage(message);

  if (shouldMaskMessage) {
    data.message = ENVIRONMENT_SERVER_MESSAGES[frontendEnv] || ENVIRONMENT_SERVER_MESSAGES.production;
    delete data.error;
  }

  if (frontendEnv !== 'local') {
    delete data.debug;
  }

  return error;
};
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
});

// Add token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {   
    sanitizeErrorResponse(error);

    const originalRequest = error.config || {};

    // Prevent infinite loops
    if (originalRequest._retry) {
      return Promise.reject(error);
    }

    // Skip redirect if the request is logout or login
    if (error.response?.status === 401 && 
        originalRequest.url !== '/logout' && 
        originalRequest.url !== '/login' &&
        !originalRequest._retry) {
      originalRequest._retry = true;
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('permissions');
      localStorage.removeItem('token_expires_at');
      window.location.href = '/login';
    }

    return Promise.reject(error);
  }
);

export const authAPI = {
  login: (credentials) => api.post('/login', credentials),
  loginWithPhone: (credentials) => api.post('/login-phone', credentials),
  logout: () => api.post('/logout').catch(error => {
    console.warn('Logout API call failed:', error);
    return Promise.resolve();
  }),
  logoutAll: () => api.post('/logout-all'),
  refresh: () => api.post('/refresh'),
  getUser: () => api.get('/user'),
  updateProfile: (data) => api.put('/profile', data),
  changePassword: (data) => api.put('/profile/password', data),
  
  // Password reset with code
  sendResetCode: (data) => api.post('/send-reset-code', data),
  verifyResetCode: (data) => api.post('/verify-reset-code', data),
  resetPasswordWithCode: (data) => api.post('/reset-password-with-code', data),
  
  // Phone password reset
  sendPhoneResetCode: (data) => api.post('/send-phone-reset-code', data),
  verifyPhoneResetCode: (data) => api.post('/verify-phone-reset-code', data),
  resetPasswordWithPhoneCode: (data) => api.post('/reset-password-with-phone-code', data),
  
  // Email verification
  verifyEmail: (data) => api.post('/verify-email', data),
  confirmEmailVerification: (data) => api.post('/confirm-email-verification', data),
  resendVerification: (data) => api.post('/resend-verification', data),
  
  // Phone verification
  verifyPhone: (data) => api.post('/verify-phone', data),
  confirmPhoneVerification: (data) => api.post('/confirm-phone-verification', data),
  resendPhoneVerification: (data) => api.post('/resend-phone-verification', data),
};

export const studentAPI = {
  getAll: (params = {}) => api.get('/students', { params }),
  getActive: (params = {}) => api.get('/students', { params: { ...params, active: 1 } }),
  getById: (id) => api.get(`/students/${id}`),
  create: (data) => {
    const headers = data instanceof FormData ? { 'Content-Type': 'multipart/form-data' } : {};
    return api.post('/students', data, { headers });
  },
  update: (id, data) => {
    const headers = data instanceof FormData ? { 'Content-Type': 'multipart/form-data' } : {};
    return api.put(`/students/${id}`, data, { headers });
  },
  updateWithFile: (id, formData) => {
    return api.post(`/students/${id}`, formData, { 
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  uploadProfilePicture: (id, formData) => {
    return api.post(`/students/${id}/profile-picture`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  deleteProfilePicture: (id) => api.delete(`/students/${id}/profile-picture`),
  delete: (id) => api.delete(`/students/${id}`),
  bulkCreate: (data) => api.post('/students/bulk', data),
  search: (params = {}) => api.get('/students/search', { params }),
  
  // Classes and Streams
  getClasses: () => api.get('/academic/all-classes'),
  getStreams: (classId) => api.get('/students/class-streams', {
    params: classId ? { class_id: classId } : {}
  }),
  
  // Import/Export with proper blob handling
  exportExcel: (params = {}) => {
    const { format = 'csv', ...filters } = params;
    const finalParams = { format, ...filters };
    console.log('API exportExcel called with:');
    console.log('- Original params:', params);
    console.log('- Format:', format);
    console.log('- Filters:', filters);
    console.log('- Final API params:', finalParams);
    
    return api.get('/students/export', { 
      params: finalParams,
      responseType: 'blob',
      headers: {
        'Accept': format === 'excel' 
          ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          : 'text/csv'
      }
    });
  },
  
  importExcel: (formData) => {
    console.log('Importing students...');
    return api.post('/students/import', formData, {
      headers: { 
        'Content-Type': 'multipart/form-data',
        'Accept': 'application/json'
      },
    });
  },
  
  downloadTemplate: () => {
    console.log('Downloading student import template...');
    return api.get('/students/template', { 
      responseType: 'blob',
      headers: {
        'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      }
    });
  },
  
  syncClasses: () => api.get('/students/import-options'),

  // Get student ledger with filters
  getLedger: (id, params = {}) => api.get(`/students/${id}/`,{params}),

  // Download ledger PDF
  downloadLedgerPdf: (id, params = {}) => {
    console.log('Downloading student ledger PDF...', params);
    return api.get(`/students/${id}/ledger/download`, {
      params,
      responseType: 'blob',
      headers: {
        'Accept': 'application/pdf'
      }
    });
  },

  downloadStudentsPdf: (params = {}) => {
    console.log('Downloading students list PDF...', params);
    return api.get('/students/download-pdf', {
      params,
      responseType: 'blob',
      headers: {
        'Accept': 'application/pdf'
      }
    });
  },

  // Student Promotion
  getStudentsForPromotion: (classStreamId) => api.get('/students/promotion/by-class-stream', {
    params: { class_stream_id: classStreamId }
  }),
  previewPromotion: (data) => api.post('/students/promotion/preview', data),
  executePromotion: (data) => api.post('/students/promotion/execute', data),

  // Bulk Status Update
  bulkUpdateStatus: (data) => api.post('/students/bulk-status', data),
};

export const feeAPI = {
  getDashboard: () => api.get('/fees/dashboard'),
  getFinancialSummary: () => api.get('/fees/dashboard'),
  getReports: (params) => api.get('/fees/reports', { params }),

  // Fee Types
  getTypes: (params = {}) => api.get('/fees/types', { params }),
  createType: (data) => api.post('/fees/types', data),
  updateType: (id, data) => api.put(`/fees/types/${id}`, data),
  deleteType: (id) => api.delete(`/fees/types/${id}`),


  // Fee Groups
  getGroups: (params = {}) => api.get('/fees/groups', { params }),
  createGroup: (data) => api.post('/fees/groups', data),
  updateGroup: (id, data) => api.put(`/fees/groups/${id}`, data),
  deleteGroup: (id) => api.delete(`/fees/groups/${id}`),
  getStudentFeeGroups: (params = {}) => api.get('/fees/student-fee-groups', { params }),
  downloadGroupsPdf: (params = {}) => {
    console.log('Downloading fee groups PDF...', params);
    return api.get('/fees/groups/download', {
      params,
      responseType: 'blob',
      headers: {
        'Accept': 'application/pdf'
      }
    });
  },

  // Get students for allocation from fee group
  getStudentsForAllocation: (params = {}) => api.get('/fees/students-for-allocation', { params }),
  
  // Bulk allocate to selected students
  bulkAllocateToStudents: (data) => api.post('/fees/bulk-allocate', data),
  
  // Individual student allocation
  allocateToStudent: (data) => api.post('/fees/allocate-student', data),

  // Fee Allocations (view only)
  getAllocations: (params = {}) => api.get('/fees/allocations', { params }),
  downloadAllocationsPdf: (params = {}) => {
    console.log('Downloading fee allocations PDF...', params);
    return api.get('/fees/allocations/download', {
      params,
      responseType: 'blob',
      headers: {
        'Accept': 'application/pdf'
      }
    });
  },

  getClassesForFeeGroups: () => api.get('/fees/classes-for-groups'),

  // Fee Payments
  getPayments: (params = {}) => api.get('/fees/payments', { params }),
  createPayment: (data) => api.post('/fees/payments', data),
  downloadPaymentsPdf: (params = {}) => {
    console.log('Downloading fee payments PDF...', params);
    return api.get('/fees/payments/download', {
      params,
      responseType: 'blob',
      headers: {
        'Accept': 'application/pdf'
      }
    });
  },

  // Send Reminders
  sendReminders: () => api.post('/fees/send-reminders'),

  // Download Reports PDF
  downloadReportsPdf: (params = {}) => {
    console.log('Downloading fee reports PDF...', params);
    return api.get('/fees/reports/download', {
      params,
      responseType: 'blob',
      headers: {
        'Accept': 'application/pdf'
      }
    });
  },

  // Student Fee Card
  getStudentFeeCard: (params) => api.get('/fees/student-fee-card', { params }),
  
  // Terms
  getTerms: () => api.get('/fees/terms'),
  
  // Class Streams
  getClassStreams: () => api.get('/academic/class-streams'),
};

// accounting
export const accountingAPI = {
  getAccounts: (params = {}) => api.get('/accounting/accounts', { params }),
  createAccount: (data) => api.post('/accounting/accounts', data),
  getVoucherHeads: () => api.get('/accounting/voucher-heads'),
  createVoucherHead: (data) => api.post('/accounting/voucher-heads', data),
  getTransactions: (params = {}) => api.get('/accounting/transactions', { params }),
  createTransaction: (data) => api.post('/accounting/transactions', data),
};

export const assessmentAPI = {
  // Assessment Types
  getAssessmentTypes: (params = {}) => api.get('/report-card/assessments/assessment-types', { params }),
  createAssessmentType: (data) => api.post('/report-card/assessments/assessment-types', data),
  getActiveAssessmentTypes: () => api.get('/report-card/assessments/assessment-types/active'),
  getAssessmentType: (id) => api.get(`/report-card/assessments/assessment-types/${id}`),
  updateAssessmentType: (id, data) => api.put(`/report-card/assessments/assessment-types/${id}`, data),
  deleteAssessmentType: (id) => api.delete(`/report-card/assessments/assessment-types/${id}`),

  // Assessment Configurations
  getAssessmentConfigurations: (params = {}) => api.get('/report-card/assessments/assessment-configurations', { params }),
  createAssessmentConfiguration: (data) => api.post('/report-card/assessments/assessment-configurations', data),
  getAssessmentConfiguration: (id) => api.get(`/report-card/assessments/assessment-configurations/${id}`),
  updateAssessmentConfiguration: (id, data) => api.put(`/report-card/assessments/assessment-configurations/${id}`, data),
  bulkUpdateAssessmentConfigurations: (data) => api.put('/report-card/assessments/assessment-configurations/bulk', data),
  deleteAssessmentConfiguration: (id) => api.delete(`/report-card/assessments/assessment-configurations/${id}`),
  getConfigurationsForPaper: (paperId, params) => api.get(`/report-card/assessments/assessment-configurations/for-paper/${paperId}`, { params }),
  validateWeights: (data) => api.post('/report-card/assessments/assessment-configurations/validate-weights', data),

  // Student Marks
  getStudentMarks: (params = {}) => api.get('/report-card/assessments/student-marks', { params }),
  createStudentMark: (data) => api.post('/report-card/assessments/student-marks', data),
  bulkCreateStudentMarks: (data) => api.post('/report-card/assessments/student-marks/bulk', data),
  superBulkCreateStudentMarks: (data) => api.post('/report-card/assessments/student-marks/super-bulk', data),
  getMarksForAssessment: (assessmentConfigId) => api.get(`/report-card/assessments/student-marks/for-assessment/${assessmentConfigId}`),
  getMySubjectPapers: () => api.get('/report-card/assessments/student-marks/my-subject-papers'),
  getMyClasses: () => api.get('/report-card/assessments/student-marks/my-classes'),
  getMyClassTeacherClasses: () => api.get('/report-card/assessments/student-marks/my-class-teacher-classes'),
  getStudentSummary: (studentId, params) => api.get(`/report-card/assessments/student-marks/student-summary/${studentId}`, { params }),
  updateStudentMark: (id, data) => api.put(`/report-card/assessments/student-marks/${id}`, data),
  deleteStudentMark: (id) => api.delete(`/report-card/assessments/student-marks/${id}`),
  exportMarks: (assessmentConfigId) => api.get('/report-card/assessments/student-marks/export', {
    params: { assessment_configuration_id: assessmentConfigId },
    responseType: 'blob',
  }),
  downloadMarksTemplate: (assessmentConfigId) => api.get('/report-card/assessments/student-marks/template', {
    params: { assessment_configuration_id: assessmentConfigId },
    responseType: 'blob',
  }),
  importMarks: (assessmentConfigId, file) => {
    const form = new FormData();
    form.append('file', file);
    form.append('assessment_configuration_id', assessmentConfigId);
    return api.post('/report-card/assessments/student-marks/import', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  // Assessment Reports
  getAssessmentProgress: (params) => api.get('/report-card/assessments/reports/assessment-progress', { params }),
  getTeacherWorkload: (teacherId, params) => api.get(`/report-card/assessments/reports/teacher-workload/${teacherId}`, { params }),
  getAllTeachersWorkload: (params) => api.get('/report-card/assessments/reports/teacher-workload', { params }),
  getAnalytics: (params) => api.get('/report-card/assessments/reports/analytics', { params }),
};

export const gradingAPI = {
  // Grading Scales
  getGradingScales: (params = {}) => api.get('/report-card/grading/scales', { params }),
  createGradingScale: (data) => api.post('/report-card/grading/scales', data),
  updateGradingScale: (id, data) => api.put(`/report-card/grading/scales/${id}`, data),
  deleteGradingScale: (id) => api.delete(`/report-card/grading/scales/${id}`),
  getActiveGradingScales: () => api.get('/report-card/grading/scales/active'),

  // Grade Boundaries
  getGradeBoundaries: (params = {}) => api.get('/report-card/grading/boundaries', { params }),
  createGradeBoundary: (data) => api.post('/report-card/grading/boundaries', data),
  updateGradeBoundary: (id, data) => api.put(`/report-card/grading/boundaries/${id}`, data),
  deleteGradeBoundary: (id) => api.delete(`/report-card/grading/boundaries/${id}`),
  getBoundariesForScale: (scaleId) => api.get(`/report-card/grading/boundaries/for-scale/${scaleId}`),

  // Class Grading Scales
  getClassGradingScales: (params = {}) => api.get('/report-card/grading/class-scales', { params }),
  assignGradingScale: (data) => api.post('/report-card/grading/class-scales', data),
  bulkAssignGradingScale: async (data) => {
    const classIds = Array.isArray(data?.class_ids) ? data.class_ids : [];
    const { class_ids, ...rest } = data || {};
    return Promise.all(classIds.map((classId) => api.post('/report-card/grading/class-scales', {
      ...rest,
      class_id: classId
    })));
  },
  updateClassGradingScale: (id, data) => api.put(`/report-card/grading/class-scales/${id}`, data),
  deleteClassGradingScale: (id) => api.delete(`/report-card/grading/class-scales/${id}`),
  getClassGradingScaleForClass: (classId, params = {}) => api.get(`/report-card/grading/class-scales/for-class/${classId}`, { params }),

  // Grade Calculation
  calculateGrade: (percentage, scaleId) => api.post(`/report-card/grading/scales/${scaleId}/get-grade`, { percentage }),
};

export const academicAPI = {
  // Classes
  getClasses: () => api.get('/academic/classes'),
  createClass: (data) => api.post('/academic/classes', data),
  updateClass: (id, data) => api.put(`/academic/classes/${id}`, data),
  deleteClass: (id) => api.delete(`/academic/classes/${id}`),
  getAllClasses: () => api.get('/academic/all-classes'),
  
  // Streams
  getStreams: () => api.get('/academic/streams'),
  createStream: (data) => api.post('/academic/streams', data),
  attachStream: (classId, data) => api.post(`/academic/classes/${classId}/streams`, data),
  detachStream: (classId, streamId) => api.delete(`/academic/classes/${classId}/streams/${streamId}`),
  
  // Class Streams
  getClassStreams: () => api.get('/academic/class-streams'),
  createClassStream: (data) => api.post('/academic/class-streams', data),
  getClassStreamsForFilter: () => api.get('/academic/class-streams-filter'),
  
  // Levels
  getLevels: () => api.get('/academic/levels'),
  createLevel: (data) => api.post('/academic/levels', data),
  updateLevel: (id, data) => api.put(`/academic/levels/${id}`, data),
  deleteLevel: (id) => api.delete(`/academic/levels/${id}`),

  // Terms
  getTerms: (params = {}) => api.get('/academic/terms', { params }),
  createTerm: (data) => api.post('/academic/terms', data),
  updateTerm: (id, data) => api.put(`/academic/terms/${id}`, data),
  deleteTerm: (id) => api.delete(`/academic/terms/${id}`),
  setCurrentTerm: (id) => api.patch(`/academic/terms/${id}/set-current`),

};

export const communicationAPI = {
  // Email
  getEmailLogs: (params = {}) => api.get('/communications/email-logs', { params }),
  sendBulkReminders: (data) => api.post('/communications/bulk-reminders', data),
  
  // SMS
  getSmsLogs: (params = {}) => api.get('/communications/sms-logs', { params }),
  getSmsStatistics: (params = {}) => api.get('/communications/sms-statistics', { params }),
  sendSms: (data) => api.post('/communications/send-sms', data),
  sendSmsToParent: (data) => api.post('/communications/send-sms-to-parent', data),
  
  // Student filtering
  getFilteredStudents: (params = {}) => api.get('/communications/filtered-students', { params }),
};

// RBAC API
export const rbacAPI = {
  getRoles: () => api.get('/rbac/roles'),
  getPermissions: () => api.get('/rbac/permissions'),
  getResources: () => api.get('/rbac/resources'),
  getUserPermissions: (userId) => api.get(`/rbac/user-permissions/${userId}`),
  assignPermissions: (data) => api.post('/rbac/user-permissions/assign', data),
  removePermission: (data) => api.delete('/rbac/user-permissions/remove', { data }),
};

// User API
export const userAPI = {
  getAll: (params = {}) => api.get('/users', { params }),
  getById: (id) => api.get(`/users/${id}`),
  create: (data) => api.post('/users', data),
  update: (id, data) => api.post(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
  bulkAction: (data) => api.post('/users/bulk-action', data),
  resetPassword: (id, data) => api.patch(`/users/${id}/reset-password`, data),
  toggleStatus: (id) => api.patch(`/users/${id}/toggle-status`),
  getRoles: () => api.get('/users/roles'),
  uploadProfilePicture: (id, formData) => api.post(`/users/${id}/profile-picture`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  deleteProfilePicture: (id) => api.delete(`/users/${id}/profile-picture`),
};

// Role API
export const roleAPI = {
  getAll: (params = {}) => api.get('/roles', { params }),
  getById: (id) => api.get(`/roles/${id}`),
  create: (data) => api.post('/roles', data),
  update: (id, data) => api.put(`/roles/${id}`, data),
  delete: (id) => api.delete(`/roles/${id}`),
  getPermissions: (id) => api.get(`/roles/${id}/permissions`),
  assignPermissions: (id, data) => api.post(`/roles/${id}/permissions`, data),
  toggleStatus: (id) => api.patch(`/roles/${id}/toggle-status`),
};

// School API
export const schoolAPI = {
  get: () => api.get('/school'),
  create: (data) => api.post('/school', data),
  update: (data) => api.put('/school', data),
  uploadLogo: (formData) => api.post('/school/logo', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  deleteLogo: () => api.delete('/school/logo'),
};

// Clearance API
export const clearanceAPI = {
  getSettings: () => api.get('/clearance/settings'),
  createSetting: (data) => api.post('/clearance/settings', data),
  updateSetting: (id, data) => api.put(`/clearance/settings/${id}`, data),
  deleteSetting: (id) => api.delete(`/clearance/settings/${id}`),
  checkEligibility: (data) => api.post('/clearance/check-eligibility', data),
  generateCards: (data) => api.post('/clearance/generate-cards', data),
  getClearedStudents: (params = {}) => api.get('/clearance/cleared-students', { params }),
  downloadCards: (data) => api.post('/clearance/download-cards', data),
};

// Teacher API
export const teacherAPI = {
  getAll: (params = {}) => api.get('/report-card/teachers', { params }),
  getById: (id) => api.get(`/report-card/teachers/${id}`),
  create: (data) => api.post('/report-card/teachers', data),
  update: (id, data) => api.put(`/report-card/teachers/${id}`, data),
  delete: (id) => api.delete(`/report-card/teachers/${id}`),
  getActive: () => api.get('/report-card/teachers/active'),
  getSchedule: (id) => api.get(`/report-card/teachers/${id}/schedule`),
};

export const reportCardAPI = {
  getSubjectPapers: (params = {}) => api.get('/report-card/subject-papers', { params }),
  storeSubjectPaper: (data) => api.post('/report-card/subject-papers', data),
  getSubjectPapersForClass: (params = {}) => api.get('/report-card/subject-papers/for-class', { params }),
  updateSubjectPaper: (id, data) => api.put(`/report-card/subject-papers/${id}`, data),
  deleteSubjectPaper: (id) => api.delete(`/report-card/subject-papers/${id}`),
  getSubjects: (params = {}) => api.get('/report-card/subjects', { params }),
  storeSubject: (data) => api.post('/report-card/subjects', data),
  getActiveSubjects: () => api.get('/report-card/subjects/active'),
  showSubject: (id) => api.get(`/report-card/subjects/${id}`),
  updateSubject: (id, data) => api.put(`/report-card/subjects/${id}`, data),
  deleteSubject: (id) => api.delete(`/report-card/subjects/${id}`),
  getClassStreamPapers: (params = {}) => api.get('/report-card/class-stream-papers', { params }),
  storeClassStreamPaper: (data) => api.post('/report-card/class-stream-papers', data),
  bulkAssignClassStreamPapers: (papers) => api.post('/report-card/class-stream-papers', { papers }),
  updateClassStreamPaper: (id, data) => api.put(`/report-card/class-stream-papers/${id}`, data),
  deleteClassStreamPaper: (id) => api.delete(`/report-card/class-stream-papers/${id}`),

  // Conduct categories and ratings
  getConductCategories: () => api.get('/report-card/conduct-categories'),
  createConductCategory: (data) => api.post('/report-card/conduct-categories', data),
  updateConductCategory: (id, data) => api.put(`/report-card/conduct-categories/${id}`, data),
  deleteConductCategory: (id) => api.delete(`/report-card/conduct-categories/${id}`),
  getConductRatings: () => api.get('/report-card/conduct-ratings'),
  createConductRating: (data) => api.post('/report-card/conduct-ratings', data),
  updateConductRating: (id, data) => api.put(`/report-card/conduct-ratings/${id}`, data),
  deleteConductRating: (id) => api.delete(`/report-card/conduct-ratings/${id}`),

  // Student conduct
  getStudentConduct: (params = {}) => api.get('/report-card/conduct', { params }),
  createStudentConduct: (data) => api.post('/report-card/conduct', data),
  bulkCreateStudentConduct: (data) => api.post('/report-card/conduct/bulk', data),
  bulkCreateStudentConduct: (data) => api.post('/report-card/conduct/bulk', data),
  getStudentConductForStudent: (studentId, params = {}) => api.get(`/report-card/conduct/student/${studentId}`, { params }),
  updateStudentConduct: (id, data) => api.put(`/report-card/conduct/${id}`, data),
  deleteStudentConduct: (id) => api.delete(`/report-card/conduct/${id}`),

  // Subject comments
  getSubjectComments: (params = {}) => api.get('/report-card/subject-comments', { params }),
  createSubjectComment: (data) => api.post('/report-card/subject-comments', data),
  getSubjectCommentsForStudent: (studentId, params = {}) => api.get(`/report-card/subject-comments/student/${studentId}`, { params }),
  updateSubjectComment: (id, data) => api.put(`/report-card/subject-comments/${id}`, data),
  deleteSubjectComment: (id) => api.delete(`/report-card/subject-comments/${id}`),

  // General comments
  getGeneralComments: (params = {}) => api.get('/report-card/general-comments', { params }),
  createGeneralComment: (data) => api.post('/report-card/general-comments', data),
  getGeneralCommentForStudent: (studentId, params = {}) => api.get(`/report-card/general-comments/student/${studentId}`, { params }),
  updateGeneralComment: (id, data) => api.put(`/report-card/general-comments/${id}`, data),
  deleteGeneralComment: (id) => api.delete(`/report-card/general-comments/${id}`),

  // Report cards
  getReportCards: (params = {}) => api.get('/report-card/report-cards', { params }),
  getReportCard: (id) => api.get(`/report-card/report-cards/${id}`),
  getReportCardForStudent: (studentId, params = {}) => api.get(`/report-card/report-cards/student/${studentId}`, { params }),
  updateReportCard: (id, data) => api.put(`/report-card/report-cards/${id}`, data),
  bulkUpdateReportCards: (data) => api.put('/report-card/report-cards-bulk', data),
  deleteReportCard: (id) => api.delete(`/report-card/report-cards/${id}`),
  publishReportCard: (id) => api.post(`/report-card/report-cards/${id}/publish`),
  bulkPublishReportCards: (data) => api.post('/report-card/report-cards-bulk-publish', data),

  // Report card templates
  getReportCardTemplates: (params = {}) => api.get('/report-card/templates', { params }),
  getReportCardTemplate: (id) => api.get(`/report-card/templates/${id}`),
  createReportCardTemplate: (data) => api.post('/report-card/templates', data),
  updateReportCardTemplate: (id, data) => api.put(`/report-card/templates/${id}`, data),
  deleteReportCardTemplate: (id) => api.delete(`/report-card/templates/${id}`),

  // Report generation
  generateReportForStudent: (data) => api.post('/report-card/generate-report/student', data),
  generateReportForClass: (data) => api.post('/report-card/generate-report/class', data),

  // Comment templates
  getCommentTemplates: (params = {}) => api.get('/report-card/comment-templates', { params }),
  createCommentTemplate: (data) => api.post('/report-card/comment-templates', data),
  updateCommentTemplate: (id, data) => api.put(`/report-card/comment-templates/${id}`, data),
  bulkCreateCommentTemplates: (data) => api.post('/report-card/comment-templates/bulk', data),
  bulkDeleteCommentTemplates: (ids) => api.delete('/report-card/comment-templates/bulk', { data: { ids } }),
  deleteCommentTemplate: (id) => api.delete(`/report-card/comment-templates/${id}`),

  // Comment context (grade boundaries derived from marks + conduct)
  getCommentContext: (studentId, params) => api.get(`/report-card/comment-context/${studentId}`, { params }),

  // Comment generation (P5 - Comment Automation V2)
  suggestCommentTemplates: (params) => api.get('/report-card/comment-templates/suggest', { params }),
  generateComments: (data) => api.post('/report-card/comments/generate', data),

  // Auto-generate subject comment from template based on mark percentage
  // Uses upsert: fetches existing comment for student+subject+term, updates or creates
  autoGenerateSubjectComment: (data) => {
    const { student_id, subject_id, term_id, academic_year, comment_text } = data;
    return api.post('/report-card/subject-comments', { student_id, subject_id, term_id, academic_year, comment_text });
  },

  autoGenerateGeneralComment: (data) => {
    const { student_id, term_id, academic_year, class_teacher_comment } = data;
    return api.post('/report-card/general-comments', { student_id, term_id, academic_year, class_teacher_comment });
  },

  bulkGenerateClassTeacherComments: (data) => api.post('/report-card/general-comments/bulk-generate', data),
};

export default api;

export const studentPortalAPI = {
  login: (data) => api.post('/student-portal/login', data),
  logout: (token) => api.post('/student-portal/logout', {}, { headers: { Authorization: `Bearer ${token}` } }),
  changePassword: (data, token) => api.put('/student-portal/change-password', data, { headers: { Authorization: `Bearer ${token}` } }),
  getProfile: (token) => api.get('/student-portal/profile', { headers: { Authorization: `Bearer ${token}` } }),
  getReportCards: (token) => api.get('/student-portal/report-cards', { headers: { Authorization: `Bearer ${token}` } }),
  getReportCard: (id, token) => api.get(`/student-portal/report-cards/${id}`, { headers: { Authorization: `Bearer ${token}` } }),
  getFeeSummary: (token) => api.get('/student-portal/fees', { headers: { Authorization: `Bearer ${token}` } }),

  // Admin
  activatePortal: (studentId, data) => api.post(`/admin/student-portal/activate/${studentId}`, data),
  deactivatePortal: (studentId) => api.post(`/admin/student-portal/deactivate/${studentId}`),
  resetPortalPassword: (studentId, data) => api.post(`/admin/student-portal/reset-password/${studentId}`, data),
  bulkActivatePortal: (data) => api.post('/admin/student-portal/bulk-activate', data),
};
